import React, { useMemo, useState } from "react";
import ReceiptModal from "./ReceiptModal";
import useWalletContext from "./useWalletContext";

function to2(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "";
  return x.toFixed(2);
}

function localToCad(localAmount, rateLocalPerCad) {
  const a = Number(localAmount);
  const r = Number(rateLocalPerCad);
  if (!Number.isFinite(a) || !Number.isFinite(r) || r <= 0) return null;
  return a / r;
}

export default function WithdrawWalletForm({ onSuccess }) {
  const { ctx, loading: ctxLoading, err: ctxErr } = useWalletContext();

  const displayCurrency = ctx?.displayCurrency || "CAD";
  const rateCadToDisplay = Number(ctx?.rateCadToDisplay || 1);

  const [localAmount, setLocalAmount] = useState("");
  const [method, setMethod] = useState("");
  const [rail, setRail] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [receipt, setReceipt] = useState(null);
  const [openReceipt, setOpenReceipt] = useState(false);

  const methods = ctx?.methods || [];
  const selected = useMemo(() => methods.find((m) => m.code === method), [methods, method]);

  React.useEffect(() => {
    if (!method && methods.length) setMethod(methods[0].code);
  }, [methods, method]);

  const amountCadPreview = useMemo(() => {
    if ((displayCurrency || "CAD") === "CAD") return localAmount ? Number(localAmount) : 0;
    const cad = localToCad(localAmount, rateCadToDisplay);
    return cad || 0;
  }, [localAmount, displayCurrency, rateCadToDisplay]);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr("");
    setLoading(true);

    try {
      let amountCad = null;

      if ((displayCurrency || "CAD") === "CAD") {
        amountCad = Number(localAmount);
      } else {
        amountCad = localToCad(localAmount, rateCadToDisplay);
      }

      if (!Number.isFinite(amountCad) || amountCad <= 0) {
        throw new Error("Montant invalide");
      }

      const payload = {
        amount: to2(amountCad), // ✅ send CAD ledger
        method,
        note,
        metadata: {
          rail: rail || null,
          localCurrency: String(displayCurrency || "CAD").toUpperCase(),
          localAmount: to2(localAmount || 0),
          rateLocalPerCad: String(Number(rateCadToDisplay || 1).toFixed(6)),
        },
      };

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/wallet/withdraw`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to withdraw");

      if (data.receipt) {
        setReceipt(data.receipt);
        setOpenReceipt(true);
      }

      setLocalAmount("");
      setNote("");
      setRail("");
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-5 max-w-xl">
      <Header title="Retirer des fonds" subtitle="Retrait soumis à validation administrateur" />
      {(ctxErr || err) && <InlineError text={ctxErr || err} />}

      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        <Field label={`Montant (${displayCurrency})`}>
          <input
            type="number"
            min="1"
            step="0.01"
            value={localAmount}
            onChange={(e) => setLocalAmount(e.target.value)}
            className={inputCls}
            required
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Équivalent ledger: <b>{to2(amountCadPreview)} CAD</b>
          </p>
        </Field>

        <Field label="Méthode">
          <select
            value={method}
            onChange={(e) => {
              setMethod(e.target.value);
              setRail("");
            }}
            className={inputCls}
            disabled={ctxLoading || !methods.length}
          >
            {methods.map((m) => (
              <option key={m.code} value={m.code}>
                {m.label}
              </option>
            ))}
          </select>
        </Field>

        {selected?.rails?.length > 0 && (
          <Field label="Opérateur">
            <select value={rail} onChange={(e) => setRail(e.target.value)} className={inputCls} required>
              <option value="" disabled>Choisir</option>
              {selected.rails.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </Field>
        )}

        <Field label="Note (optionnel)">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={inputCls}
            placeholder="Ex: Retrait"
          />
        </Field>

        <button disabled={loading || ctxLoading} className={primaryBtn}>
          {loading ? "Traitement..." : "Confirmer"}
        </button>
      </form>

      <ReceiptModal
        open={openReceipt}
        receipt={receipt}
        onClose={() => {
          setOpenReceipt(false);
          setReceipt(null);
          onSuccess && onSuccess();
        }}
      />
    </section>
  );
}

function Header({ title, subtitle }) {
  return (
    <div>
      <h3 className="text-base font-semibold text-slate-900 dark:text-white">{title}</h3>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">{label}</label>
      {children}
    </div>
  );
}
function InlineError({ text }) {
  return (
    <div className="mt-4 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-300">
      {text}
    </div>
  );
}

const inputCls =
  "w-full rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-950 px-4 py-3 text-slate-900 dark:text-white outline-none " +
  "focus:ring-2 focus:ring-slate-900/10 dark:focus:ring-white/10";

const primaryBtn =
  "w-full rounded-2xl bg-slate-900 text-white py-3 font-medium hover:opacity-95 transition disabled:opacity-60 dark:bg-white dark:text-slate-900";
